import Chip from '@mui/material/Chip'
import type { ChipProps } from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import type { AuditPlanStatus, UserEntity, UserStatus } from '@/types/api'
import { AUDIT_PLAN_STATUS_LABELS, ENTITY_LABELS, STATUS_LABELS } from '@/lib/format'
import { formatPermission } from '@/lib/permissions'

const STATUS_COLORS: Record<UserStatus, ChipProps['color']> = {
  PENDING_EMAIL_VERIFICATION: 'warning',
  PENDING_ID_SUBMISSION: 'warning',
  PENDING_REVIEW: 'warning',
  PENDING_ACTIVATION: 'info',
  APPROVED: 'success',
  REJECTED: 'error',
}

/**
 * Entity badge. Kept minimal and neutral: the administrator badge is filled and
 * the staff badge is outlined, so the two read differently without colour noise.
 */
export function EntityChip({ entity, size = 'small' }: { entity: UserEntity; size?: ChipProps['size'] }) {
  return <Chip size={size} label={ENTITY_LABELS[entity]} variant={entity === 'ADMIN' ? 'filled' : 'outlined'} />
}

export function AccountStatusChip({ status, size = 'small' }: { status: UserStatus; size?: ChipProps['size'] }) {
  return <Chip size={size} color={STATUS_COLORS[status]} label={STATUS_LABELS[status]} variant="outlined" />
}

export function ActiveChip({ active, size = 'small' }: { active: boolean; size?: ChipProps['size'] }) {
  return <Chip size={size} color={active ? 'success' : 'default'} label={active ? 'Active' : 'Inactive'} variant="outlined" />
}

/**
 * Badge for built-in groups the platform manages itself (Staff,
 * Administrators). The lock icon marks them as protected: they cannot be
 * renamed or deleted, unlike the groups users create.
 */
export function SystemChip({ size = 'small' }: { size?: ChipProps['size'] }) {
  return <Chip size={size} variant="outlined" icon={<LockOutlinedIcon />} label="System" />
}

export function PermissionChip({ code }: { code: string }) {
  return (
    <Tooltip title={code}>
      <Chip size="small" variant="outlined" label={formatPermission(code)} />
    </Tooltip>
  )
}

const AUDIT_PLAN_STATUS_COLORS: Record<AuditPlanStatus, ChipProps['color']> = {
  DRAFT: 'default',
  SUBMITTED: 'info',
  APPROVED: 'success',
  RETURNED: 'warning',
  ARCHIVED: 'default',
}

/** Lifecycle badge for an audit plan. */
export function AuditPlanStatusChip({
  status,
  size = 'small',
}: {
  status: AuditPlanStatus
  size?: ChipProps['size']
}) {
  return (
    <Chip
      size={size}
      color={AUDIT_PLAN_STATUS_COLORS[status]}
      label={AUDIT_PLAN_STATUS_LABELS[status]}
      variant={status === 'DRAFT' ? 'outlined' : 'filled'}
    />
  )
}
